// Helper function to convert "HH:MM" to minutes since midnight
import { t } from "i18next";
import { length } from "ramda";
import { isNilOrEmpty } from "ramda-adjunct";
import { array, boolean, InferType, number, object, string, ValidationError } from "yup";

function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
}

export const timeRange = object({
  from: string().defined().default(null).nullable(),
  to: string().defined().default(null).nullable(),
}).test(
  "is-after-from",
  () => t("endTimeBeforeStart"),
  function (value, { path }) {
    const { from, to } = value;
    if (!from || !to)
      return new ValidationError([
        new ValidationError(t("required"), from, path + ".from"),
        new ValidationError(t("required"), to, path + ".to"),
      ]);

    // Convert time strings to comparable format (minutes since midnight)
    const fromMinutes = timeToMinutes(from);
    const toMinutes = timeToMinutes(to);

    if (toMinutes > fromMinutes || (toMinutes === 0 && toMinutes !== fromMinutes)) {
      return true;
    }
    return new ValidationError([
      new ValidationError(t("endTimeBeforeStart"), from, path + ".from"),
      new ValidationError(t("endTimeBeforeStart"), to, path + ".to"),
    ]);
  }
);
export const weekDaySchema = object({
  weekDay: number().min(1).max(7).required(),
  enabled: boolean().default(false),
  times: array()
    .of(timeRange)
    .test(
      "no-overlapping-intervals",
      () => t("timeOverlapping"),
      function (value) {
        if (isNilOrEmpty(value) || length(value ?? []) <= 1) return true; // No overlap possible with 0 or 1 intervals

        return !value?.some((interval) => {
          const { from, to } = interval;
          if (!from || !to) return false; // Skip validation if either value is missing

          const fromMinutes = timeToMinutes(from);
          const toMinutes = timeToMinutes(to);
          return value?.some((otherInterval) => {
            const { from: otherFrom, to: otherTo } = otherInterval;
            if (!otherFrom || !otherTo) return false; // Skip validation if either value is missing

            const otherFromMinutes = timeToMinutes(otherFrom);
            const otherToMinutes = timeToMinutes(otherTo);
            return interval !== otherInterval && otherToMinutes >= fromMinutes && otherFromMinutes <= toMinutes;
          });
        });
      }
    )
    .default([]),
});
export const intervalTypeTimeSchema = object({
  daysOfWeek: array()
    .of(weekDaySchema)
    .default(
      Array.from({ length: 7 }, (_, i) => ({
        ...weekDaySchema.getDefault(),
        weekDay: i + 1,
      }))
    ),
});

export type WeekDaySchema = InferType<typeof weekDaySchema>;
